import React, { useRef, useState, useEffect, useMemo } from 'react';
import { BookOpen, Download, Edit3, FileText, Plus, X } from 'lucide-react';
import { money, saveAdminBook, softDeleteBook, saveSiteSettings } from '../lib/compat.js';
import { db, fs, storage } from '../lib/firebase.js';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { exportToExcel } from './exports.js';
import { AdminFilterBar, AdminModal, EmptyState, Pager, usePagedRows } from './shared.jsx';

function bookPrice(book) {
  const prices = (book.variants || []).map((v) => Number(v.price || 0)).filter(Boolean);
  return prices.length ? Math.min(...prices) : 0;
}

async function uploadFile(file, path) {
  if (!file) return null;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  return getDownloadURL(r);
}

async function uploadImage(file, path) {
  return uploadFile(file, path);
}

// ─── Rich Text Editor (contenteditable) ────────────────────────────────────
function RichTextEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);

  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML && value) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  function exec(cmd, val) {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val || null);
    sync();
  }

  function sync() {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }

  return (
    <div style={{ border: '1.5px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      {/* Toolbar */}
      <div style={{ background: '#F8F8F8', borderBottom: '1px solid var(--admin-border)', padding: '5px 8px', display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
        {[['B', 'bold', <b>B</b>], ['I', 'italic', <i>I</i>], ['U', 'underline', <u>U</u>], ['S', 'strikeThrough', <s>S</s>]].map(([t, c, label]) => (
          <button key={t} type="button" className="rteBtn" title={t} onMouseDown={(e) => { e.preventDefault(); exec(c); }}>{label}</button>
        ))}
        <div className="rteSep" />
        <button type="button" className="rteBtn" onMouseDown={(e) => { e.preventDefault(); exec('justifyLeft'); }}>≡</button>
        <button type="button" className="rteBtn" onMouseDown={(e) => { e.preventDefault(); exec('justifyCenter'); }}>☰</button>
        <button type="button" className="rteBtn" onMouseDown={(e) => { e.preventDefault(); exec('justifyRight'); }}>≣</button>
        <div className="rteSep" />
        <button type="button" className="rteBtn" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}>• ≡</button>
        <button type="button" className="rteBtn" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}>1. ≡</button>
        <div className="rteSep" />
        <button type="button" className="rteBtn" onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}>✕A</button>
        <button
          type="button"
          className={`rteBtn${showSource ? ' rteBtn--active' : ''}`}
          style={{ fontSize: 10, fontWeight: 700, marginLeft: 'auto' }}
          onClick={() => {
            if (!showSource && editorRef.current) onChange(editorRef.current.innerHTML);
            setShowSource(!showSource);
          }}
        >&lt;/&gt; HTML</button>
      </div>
      {/* Editor */}
      {!showSource ? (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          style={{ minHeight: 140, padding: '10px 12px', fontSize: 13, lineHeight: 1.7, outline: 'none', overflowY: 'auto', maxHeight: 300, fontFamily: 'Arial, sans-serif' }}
          onInput={sync}
          onPaste={() => setTimeout(sync, 10)}
        />
      ) : (
        <textarea
          value={value || ''}
          onChange={(e) => {
            onChange(e.target.value);
            if (editorRef.current) editorRef.current.innerHTML = e.target.value;
          }}
          style={{ width: '100%', minHeight: 140, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6, border: 'none', outline: 'none', resize: 'vertical' }}
        />
      )}
    </div>
  );
}

export default function AdminBooks({ data, query, onRefresh }) {
  const [cat, setCat] = useState('');
  const [type, setType] = useState('');
  const [editing, setEditing] = useState(null);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const categories = useMemo(() => [...new Set(data.books.map((b) => b.cat).filter(Boolean))].sort(), [data.books]);
  const needle = query.trim().toLowerCase();
  const rows = useMemo(() => data.books.filter((b) => {
    const productType = b.productType || (b.ebook?.enabled ? 'ebook' : 'physical');
    return (!needle || [b.title, b.cat, b.seoSlug, productType].join(' ').toLowerCase().includes(needle)) && (!cat || b.cat === cat) && (!type || productType === type);
  }), [data.books, needle, cat, type]);
  const pager = usePagedRows(rows, 20);

  async function remove(book) {
    if (!window.confirm(`Hide "${book.title}" from store?`)) return;
    await softDeleteBook(book.id);
    await onRefresh();
  }

  function exportBooks() {
    exportToExcel('muppadai-books', data.books.map((b) => ({
      ID: b.id, Title: b.title, Category: b.cat,
      Price: bookPrice(b), Type: b.productType || (b.ebook?.enabled ? 'ebook' : 'physical'), Active: b.active !== false ? 'Yes' : 'No',
      InStock: b.inStock !== false ? 'Yes' : 'No',
      Variants: (b.variants || []).map((v) => `${v.name} ₹${v.price}`).join(', ')
    })), 'Books');
  }

  return (
    <>
      <AdminFilterBar>
        <select value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map((c) => <option value={c} key={c}>{c}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All Types</option>
          <option value="physical">Physical Books</option>
          <option value="ebook">E-Books</option>
          <option value="combo">Book + E-Book</option>
        </select>
        <button type="button" onClick={() => { setCat(''); setType(''); }}>Clear</button>
        <button type="button" onClick={() => setCatMgrOpen(true)}>⚙ Categories</button>
        <button type="button" className="btn-green" onClick={() => setEditing({ cat: categories[0] || 'TNUSRB', productType: 'physical', active: true, inStock: true, variants: [{ name: '', price: '', inStock: true }] })}>
          <Plus size={14} /> Add Book
        </button>
        <button type="button" className="btn-primary" onClick={() => setEditing({ cat: categories[0] || 'TNUSRB', productType: 'ebook', active: true, inStock: true, ebook: { enabled: true, format: 'PDF' }, variants: [{ name: '', price: '', inStock: true }] })}>
          <FileText size={14} /> Add E-Book
        </button>
        <button type="button" className="btn-export-xl" onClick={exportBooks}><Download size={14} /> Excel</button>
      </AdminFilterBar>

      <div className="adminCard">
        <div className="adminCardHead"><h3>Book Catalog</h3><span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{rows.length} book(s)</span></div>
        <div className="tableWrap">
          <table>
            <thead>
              <tr><th>Image</th><th>Title</th><th>Category</th><th>Type</th><th>Pricing</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {pager.paged.map((book) => {
                const active = book.active !== false;
                const inStock = book.inStock !== false;
                return (
                  <tr key={book.id}>
                    <td>
                      <div className="bookThumb">
                        {book.imageUrl ? <img src={book.imageUrl} alt="" /> : <BookOpen size={20} />}
                      </div>
                    </td>
                    <td>
                      <strong>{book.title || 'Untitled'}</strong>
                      <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{book.id}</div>
                    </td>
                    <td><span className={`badge b-${(book.cat || 'other').toLowerCase().replace(/\s/g, '')}`}>{book.cat || '—'}</span></td>
                    <td><span className="badge b-proc">{book.productType === 'combo' ? 'Book + E-Book' : book.productType === 'ebook' || book.ebook?.enabled ? 'E-Book' : 'Physical'}</span></td>
                    <td>
                      {money(bookPrice(book))}
                      <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{book.variants?.length || 0} variant(s)</div>
                    </td>
                    <td>
                      {!active ? <span className="badge b-hidden">Hidden</span> : !inStock ? <span className="badge b-proc">Out of Stock</span> : <span className="badge b-active">Active</span>}
                    </td>
                    <td>
                      <div className="rowActions">
                        <button type="button" onClick={() => setEditing(book)}><Edit3 size={13} /> Edit</button>
                        <button type="button" onClick={() => remove(book)} style={{ color: 'var(--admin-red)' }}><X size={13} /> Hide</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <EmptyState title="No books found" body="Try adjusting your search or category filter." />}
        </div>
        <Pager state={pager} total={rows.length} />
      </div>

      {catMgrOpen && <CategoryManager categories={categories} onClose={() => setCatMgrOpen(false)} onRefresh={onRefresh} />}
      {editing && <BookFormModal book={editing} categories={categories} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await onRefresh(); }} />}
    </>
  );
}

function CategoryManager({ categories, onClose, onRefresh }) {
  const [newCat, setNewCat] = useState('');
  const [msg, setMsg] = useState('');

  async function addCategory() {
    const name = newCat.trim().toUpperCase();
    if (!name) return;
    // Save a placeholder book category or just note (categories are derived from books)
    try {
      await fs.setDoc(fs.doc(db, 'settings', 'categories'), { [name]: true, updatedAt: new Date().toISOString() }, { merge: true });
      setMsg(`Category "${name}" saved.`);
      setNewCat('');
      await onRefresh();
    } catch (e) {
      setMsg('Error: ' + e.message);
    }
  }

  return (
    <AdminModal title="Manage Categories" onClose={onClose}>
      {msg && <div className="banner compact">{msg}</div>}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input value={newCat} onChange={(e) => setNewCat(e.target.value.toUpperCase())} placeholder="e.g. STATE GOVT" style={{ flex: 1, padding: '8px 10px', border: '1.5px solid var(--admin-border)', borderRadius: 8, fontSize: 13 }} />
        <button type="button" className="btn-primary" onClick={addCategory}>Add</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {categories.map((c) => (
          <span key={c} className="badge" style={{ background: 'var(--cream-dk)', color: 'var(--admin-navy)', fontSize: 12, padding: '5px 12px' }}>{c}</span>
        ))}
      </div>
    </AdminModal>
  );
}

function BookFormModal({ book, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...book,
    productType: book.productType || (book.ebook?.enabled ? 'ebook' : 'physical'),
    ebook: {
      enabled: book.productType === 'ebook' || book.productType === 'combo' || book.ebook?.enabled === true,
      format: book.ebook?.format || 'PDF',
      pages: book.ebook?.pages || '',
      sampleUrl: book.ebook?.sampleUrl || '',
      downloadUrl: book.ebook?.downloadUrl || '',
      fileName: book.ebook?.fileName || '',
      deliveryMode: book.ebook?.deliveryMode || '',
      accessNote: book.ebook?.accessNote || '',
      licenseText: book.ebook?.licenseText || ''
    },
    variants: book.variants?.length ? book.variants : [{ name: '', price: '', inStock: true, imageUrl: '', samplePdfUrl: '', restockDate: '', restockMsg: '' }]
  });
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(book.imageUrl || '');
  const [sampleFiles, setSampleFiles] = useState([]);
  const [ebookFile, setEbookFile] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const detailPointsText = Array.isArray(form.detailPoints) ? form.detailPoints.join('\n') : form.detailPoints || '';
  const sampleImagesText = Array.isArray(form.sampleImages) ? form.sampleImages.join('\n') : form.sampleImages || '';
  const faqsText = Array.isArray(form.faqs)
    ? form.faqs.map((item) => `${item.q || item.question || ''} | ${item.a || item.answer || ''}`).join('\n')
    : form.faqs || '';

  function setVariant(idx, patch) {
    setForm({ ...form, variants: form.variants.map((v, i) => i === idx ? { ...v, ...patch } : v) });
  }

  function setEbook(patch) {
    setForm({ ...form, ebook: { ...(form.ebook || {}), ...patch } });
  }

  function addVariant() {
    setForm({ ...form, variants: [...form.variants, { name: '', price: '', inStock: true, imageUrl: '', samplePdfUrl: '', restockDate: '', restockMsg: '' }] });
  }

  function handleImgFile(file) {
    if (!file) return;
    setImgFile(file);
    const url = URL.createObjectURL(file);
    setImgPreview(url);
  }

  async function save() {
    setMsg('');
    setBusy(true);
    try {
      let imageUrl = form.imageUrl || '';
      if (imgFile) {
        const path = `books/${book.id || Date.now()}_${imgFile.name}`;
        imageUrl = await uploadImage(imgFile, path);
      }
      const uploadedSamples = [];
      for (const file of sampleFiles) {
        uploadedSamples.push(await uploadImage(file, `books/samples/${book.id || Date.now()}_${Date.now()}_${file.name}`));
      }
      const ebook = { ...(form.ebook || {}) };
      if (ebookFile) {
        ebook.downloadUrl = await uploadFile(ebookFile, `books/ebooks/${book.id || Date.now()}_${Date.now()}_${ebookFile.name}`);
        ebook.fileName = ebookFile.name;
        ebook.format = ebook.format || 'PDF';
      }
      const sampleImages = [...(Array.isArray(form.sampleImages) ? form.sampleImages : String(form.sampleImages || '').split('\n')), ...uploadedSamples]
        .map((url) => String(url || '').trim())
        .filter(Boolean);
      await saveAdminBook({ ...form, ebook, imageUrl, sampleImages });
      await onSaved();
    } catch (e) {
      setMsg(e.message || 'Unable to save book.');
    } finally {
      setBusy(false);
    }
  }

  const catList = [...new Set([...categories, form.cat || ''].filter(Boolean))];

  return (
    <AdminModal title={book.id ? 'Edit Book' : 'Add New Book'} onClose={onClose} wide>
      {msg && <div className="banner danger compact">{msg}</div>}
      <div style={{ background: '#FFFDE7', border: '1px solid #F0C040', borderRadius: 8, padding: '9px 12px', fontSize: 12, color: '#7A5C00', marginBottom: 12 }}>
        💡 Changes go live on the store immediately.
      </div>
      <div className="adminFormGrid">
        <label className="full">Book Title *<input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. TNUSRB SI Books (Combo)" /></label>
        <label>
          Category *
          <select value={form.cat || ''} onChange={(e) => setForm({ ...form, cat: e.target.value })}>
            {catList.map((c) => <option value={c} key={c}>{c}</option>)}
          </select>
        </label>
        <label>
          Product Type *
          <select
            value={form.productType || 'physical'}
            onChange={(e) => {
              const productType = e.target.value;
              setForm({ ...form, productType, ebook: { ...(form.ebook || {}), enabled: productType === 'ebook' || productType === 'combo' } });
            }}
          >
            <option value="physical">Physical Book</option>
            <option value="ebook">E-Book</option>
            <option value="combo">Physical + E-Book</option>
          </select>
        </label>
        <label>Sort Order<input type="number" value={form.order || ''} onChange={(e) => setForm({ ...form, order: e.target.value })} /></label>
        <label>SEO Slug<input value={form.seoSlug || ''} onChange={(e) => setForm({ ...form, seoSlug: e.target.value })} placeholder="tnusrb-si-tamil-books" /></label>
        <label className="full">SEO Title<input value={form.seoTitle || ''} onChange={(e) => setForm({ ...form, seoTitle: e.target.value })} placeholder="TNUSRB SI Tamil Books 2026 | Muppadai" /></label>
        <label className="full">Meta Description<textarea rows={2} value={form.metaDescription || ''} onChange={(e) => setForm({ ...form, metaDescription: e.target.value })} placeholder="Short Google snippet for this product page." /></label>
        <label className="full">Focus Keywords<input value={form.focusKeywords || ''} onChange={(e) => setForm({ ...form, focusKeywords: e.target.value })} placeholder="tnusrb si tamil books, tnusrb si study material" /></label>
        <div className="full" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ width: 90, height: 70, borderRadius: 8, border: '1.5px solid var(--admin-border)', background: 'var(--cream-dk)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {imgPreview ? <img src={imgPreview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <BookOpen size={28} />}
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Upload Image
              <input type="file" accept="image/*" onChange={(e) => handleImgFile(e.target.files?.[0])} style={{ display: 'block', marginTop: 4, fontSize: 12 }} />
            </label>
            <label>
              Or Image URL
              <input value={form.imageUrl || ''} placeholder="https://…" onChange={(e) => { setForm({ ...form, imageUrl: e.target.value }); setImgPreview(e.target.value); }} />
            </label>
          </div>
        </div>
        {(form.productType === 'ebook' || form.productType === 'combo') && (
          <div className="full adminSubPanel">
            <div className="adminSubPanelHead">
              <FileText size={16} />
              <strong>E-Book Details</strong>
              <span>Shown on the product page and E-Books category</span>
            </div>
            <div className="adminFormGrid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label>Format<input value={form.ebook?.format || ''} onChange={(e) => setEbook({ format: e.target.value })} placeholder="PDF" /></label>
              <label>Pages<input value={form.ebook?.pages || ''} onChange={(e) => setEbook({ pages: e.target.value })} placeholder="250 pages" /></label>
              <label className="full">E-Book Sample URL<input value={form.ebook?.sampleUrl || ''} onChange={(e) => setEbook({ sampleUrl: e.target.value })} placeholder="https://.../sample.pdf" /></label>
              <label className="full">Upload Paid E-Book PDF
                <input type="file" accept="application/pdf,.pdf" onChange={(e) => setEbookFile(e.target.files?.[0] || null)} style={{ display: 'block', marginTop: 4, fontSize: 12 }} />
                {(ebookFile || form.ebook?.fileName) && <span style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{ebookFile?.name || form.ebook?.fileName}</span>}
              </label>
              <label className="full">Paid E-Book Download URL<input value={form.ebook?.downloadUrl || ''} onChange={(e) => setEbook({ downloadUrl: e.target.value })} placeholder="https://.../paid-book.pdf" /></label>
              <label className="full">Access / Delivery Mode<input value={form.ebook?.deliveryMode || ''} onChange={(e) => setEbook({ deliveryMode: e.target.value })} placeholder="Shared after payment verification / Instant access" /></label>
              <label className="full">Access Note<textarea rows={3} value={form.ebook?.accessNote || ''} onChange={(e) => setEbook({ accessNote: e.target.value })} placeholder="Tell students how the e-book access will be delivered after payment." /></label>
              <label className="full">License / Usage Note<textarea rows={2} value={form.ebook?.licenseText || ''} onChange={(e) => setEbook({ licenseText: e.target.value })} placeholder="For personal exam preparation only. Do not share or resell." /></label>
            </div>
          </div>
        )}
        <div className="full">
          <label style={{ display: 'block', marginBottom: 6 }}>Description <span style={{ fontSize: 10, color: 'var(--admin-muted)', fontWeight: 400 }}>— Rich Text / HTML</span></label>
          <RichTextEditor value={form.description || ''} onChange={(v) => setForm({ ...form, description: v })} />
        </div>
        <label className="full">What Students Get <span style={{ fontSize: 10, color: 'var(--admin-muted)', fontWeight: 400 }}>- one point per line</span>
          <textarea rows={4} value={detailPointsText} onChange={(e) => setForm({ ...form, detailPoints: e.target.value })} placeholder={'Complete syllabus coverage\nChapter-wise practice questions\nTamil / English medium options'} />
        </label>
        <label className="full">Sample Preview Image URLs <span style={{ fontSize: 10, color: 'var(--admin-muted)', fontWeight: 400 }}>- one image URL per line</span>
          <textarea rows={4} value={sampleImagesText} onChange={(e) => setForm({ ...form, sampleImages: e.target.value })} placeholder={'https://.../sample-page-1.jpg\nhttps://.../sample-page-2.jpg'} />
        </label>
        <label className="full">Upload Sample Preview Images
          <input type="file" accept="image/*" multiple onChange={(e) => setSampleFiles(Array.from(e.target.files || []))} style={{ display: 'block', marginTop: 4, fontSize: 12 }} />
          {!!sampleFiles.length && <span style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{sampleFiles.length} image(s) selected</span>}
        </label>
        <label className="full">Book FAQ <span style={{ fontSize: 10, color: 'var(--admin-muted)', fontWeight: 400 }}>- format: Question | Answer</span>
          <textarea rows={4} value={faqsText} onChange={(e) => setForm({ ...form, faqs: e.target.value })} placeholder={'Is this latest syllabus? | Yes, this book is prepared for the latest exam pattern.\nWhen will I receive it? | Usually within 2-4 working days.'} />
        </label>
      </div>

      {/* Variants */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 8px' }}>
        <strong style={{ fontSize: 13 }}>Variants &amp; Pricing *</strong>
        <button type="button" onClick={addVariant}><Plus size={13} /> Add Variant</button>
      </div>
      {form.variants.map((v, idx) => (
        <div key={idx} style={{ border: '1px solid var(--admin-border)', borderRadius: 10, padding: '12px', marginBottom: 8, background: '#F8F8FF', position: 'relative' }}>
          <div className="adminFormGrid" style={{ gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label>Variant Name *<input value={v.name || v.vn || ''} onChange={(e) => setVariant(idx, { name: e.target.value })} placeholder="e.g. 4-Book Combo" /></label>
            <label>Price (₹) *<input type="number" value={v.price || ''} onChange={(e) => setVariant(idx, { price: e.target.value })} placeholder="499" /></label>
            <label>Variant Image URL<input value={v.imageUrl || ''} onChange={(e) => setVariant(idx, { imageUrl: e.target.value })} placeholder="https://…" /></label>
            <label>Sample PDF URL<input value={v.samplePdfUrl || ''} onChange={(e) => setVariant(idx, { samplePdfUrl: e.target.value })} placeholder="https://…" /></label>
            <label>Restock Date<input type="date" value={v.restockDate || ''} onChange={(e) => setVariant(idx, { restockDate: e.target.value })} /></label>
            <label>Restock Message<input value={v.restockMsg || ''} onChange={(e) => setVariant(idx, { restockMsg: e.target.value })} placeholder="Back in stock on…" /></label>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={v.inStock !== false} onChange={(e) => setVariant(idx, { inStock: e.target.checked })} />
              In Stock
            </label>
          </div>
          {form.variants.length > 1 && (
            <button type="button" onClick={() => setForm({ ...form, variants: form.variants.filter((_, i) => i !== idx) })} style={{ position: 'absolute', top: 8, right: 8, background: '#FECACA', border: 'none', color: '#B91C1C', width: 26, height: 26, borderRadius: 6, cursor: 'pointer' }}>✕</button>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Show on store (Active)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={form.inStock !== false} onChange={(e) => setForm({ ...form, inStock: e.target.checked })} />
          Book In Stock
        </label>
      </div>

      <div className="modalActions" style={{ marginTop: 16 }}>
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save Book'}</button>
      </div>
    </AdminModal>
  );
}
